<template>
  <section class="quiz">
    <div class="quiz-body" v-if="quiz">
      <p class="title">{{ isChild ? t('dearChild') : t('dear') }}</p>
      <p class="text">{{ quiz.descripton || quiz.text }}</p>
      <p class="answer-count">{{ t('quistions') }}: {{ questions.length }}</p>
      <p class="school-name" v-if="quiz.organization_name">{{ quiz.organization_name }}</p>

      <div v-for="(item, index) in questions" :key="item.id" class="question">
        <span class="q-num">{{ t('quistion') }} №{{ index + 1 }}</span>
        <span class="q-text">{{ item.label }}</span>

        <div class="quiz-answer" v-if="item.type === 1">
          <select v-model="item.answer" @change="sendSelectAnswer(item)">
            <option :value="-1">-</option>
            <option v-for="answer in item.answers || []" :key="answer.id" :value="answer.id">{{ answer.label }}</option>
          </select>
        </div>

        <div class="quiz-answer" v-if="item.type === 2">
          <textarea v-model.trim="item.answer" @blur="sendTextAnswer(item)"></textarea>
        </div>

        <div v-if="item.type === 3">
          <div v-for="answer in item.answers || []" :key="answer.id">
            <li class="q-text q-li">{{ answer.label }}</li>
            <div class="answers">
              <button
                v-for="score in 10"
                :key="`${answer.id}-${score}`"
                type="button"
                class="answer-div"
                :class="{ answered: score === answer.answer }"
                @click="sendScaleAnswer(item.id, answer.id, score)"
              >
                <span class="q-answer">{{ score }}</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="item.type === 4" class="quiz-answer checkbox-answers">
          <label v-for="answer in item.answers || []" :key="answer.id" class="checkbox-answer">
            <input v-model="item.answer" type="checkbox" :value="answer.id" @change="sendCheckboxAnswer(item)" />
            <span>{{ answer.label }}</span>
          </label>
        </div>
      </div>
    </div>

    <button type="button" class="end-survey mt-4" :disabled="isFinishDisabled" @click="finishQuiz">
      {{ t('endSurvey') }}
    </button>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useFeedQuizDetailQuery, useQuizAnswerMutation } from '@/queries';
import { useTopbarStore } from '@/stores/topbar';
import { useViewerStore } from '@/stores/viewer';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const topbarStore = useTopbarStore();
const viewerStore = useViewerStore();
const answerMutation = useQuizAnswerMutation();

const quizId = computed(() => route.params.quizId);
const quizDetailQuery = useFeedQuizDetailQuery(quizId);
const questions = ref([]);

const quiz = computed(() => quizDetailQuery.data.value || null);
const isChild = computed(() => viewerStore.role === 'child');

function mapQuestions(item) {
  const answersMap = new Map();
  (item?.answers || []).forEach((answer) => {
    const questionAnswers = answersMap.get(answer.question_id) || [];
    questionAnswers.push(answer);
    answersMap.set(answer.question_id, questionAnswers);
  });

  return (item?.questions || []).map((question) => {
    const type = Number(question.type);
    const savedAnswers = answersMap.get(question.id) || [];
    const saved = savedAnswers[0];
    if (type === 3) {
      const nextAnswers = (question.answers || []).map((answer) => ({
        ...answer,
        answer: Number(saved?.result || -1)
      }));
      return { ...question, type, answers: nextAnswers };
    }
    if (type === 2) {
      return {
        ...question,
        type,
        answer: typeof saved?.result === 'string' ? saved.result : ''
      };
    }
    if (type === 4) {
      return {
        ...question,
        type,
        answer: normalizeMultipleAnswers(savedAnswers)
      };
    }
    return { ...question, type, answer: Number(saved?.result || -1) };
  });
}

function normalizeMultipleAnswers(savedAnswers) {
  const values = savedAnswers.flatMap((saved) => {
    const result = saved?.result;
    if (Array.isArray(result)) return result;
    if (typeof result === 'number') return [result];
    if (typeof result !== 'string' || !result.trim()) return [];

    try {
      const parsed = JSON.parse(result);
      if (Array.isArray(parsed)) return parsed;
      return [parsed];
    } catch {
      return result.split(',');
    }
  });

  return [...new Set(values.map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0))];
}

watch(
  quiz,
  (value) => {
    if (!value) return;
    questions.value = mapQuestions(value);
    topbarStore.setTitle(value.title || t('feed'));
  },
  { immediate: true }
);

const isFinishDisabled = computed(() =>
  questions.value.some((question) => {
    if (question.type === 3) {
      return (question.answers || []).some((answer) => Number(answer.answer) < 0);
    }
    if (question.type === 2) {
      return !question.answer;
    }
    if (question.type === 4) {
      return !Array.isArray(question.answer) || question.answer.length === 0;
    }
    return Number(question.answer) < 0;
  })
);

function sendSelectAnswer(item) {
  if (Number(item.answer) < 0) return;
  answerMutation.mutate({
    question_id: item.id,
    answer_id: item.answer
  });
}

function sendTextAnswer(item) {
  if (!item.answer) return;
  answerMutation.mutate({
    question_id: item.id,
    answer_text: item.answer
  });
}

function sendCheckboxAnswer(item) {
  answerMutation.mutate({
    question_id: item.id,
    answer_id: [...item.answer]
  });
}

function sendScaleAnswer(questionId, answerId, score) {
  const question = questions.value.find((item) => item.id === questionId);
  if (!question) return;
  const target = (question.answers || []).find((item) => item.id === answerId);
  if (!target) return;

  target.answer = score;
  answerMutation.mutate({
    question_id: questionId,
    answer_id: answerId,
    answer_text: String(score)
  });
}

function finishQuiz() {
  router.push('/feed');
}

onMounted(() => {
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => router.push('/feed')
  });
});
</script>

<style scoped>
.quiz {
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.school-name {
  font-weight: 700;
  font-size: 16px;
  color: #007aff;
  border-bottom: 1px dashed #bdbdbd;
  padding-bottom: 12px;
}

.question {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  padding-bottom: 12px;
}

.quiz-body .question:not(:last-child) {
  border-bottom: 1px dashed #bdbdbd;
}

.answers {
  display: flex;
  max-width: 311px;
  border: 1px solid #007aff;
  border-radius: 4px;
  margin-top: 8px;
}

.answer-div {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fff;
  border-right: 1px solid #007aff;
}

.answer-div:last-child {
  border-right: none;
}

.answered {
  background: #007aff;
}

.answered .q-answer {
  color: #fff;
}

.quiz-answer {
  margin-top: 8px;
}

.quiz-answer select,
.quiz-answer textarea {
  width: 100%;
  max-width: 600px;
  border-radius: 4px;
}

.quiz-answer select {
  height: 36px;
  padding-inline: 8px;
}

.checkbox-answers {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checkbox-answer {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #4f4f4f;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
}

.checkbox-answer input {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: #007aff;
}

.q-num {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #4f4f4f;
}

.q-text {
  font-size: 13px;
  line-height: 18px;
  color: #4f4f4f;
  margin-top: 8px;
}

.q-li {
  margin-left: 24px;
}

.q-answer {
  font-size: 14px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.8);
}

.title {
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
  color: #4f4f4f;
  text-align: center;
  margin-top: 12px;
}

.text {
  font-size: 16px;
  line-height: 18px;
  text-align: justify;
  color: rgba(0, 0, 0, 0.8);
}

.answer-count {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #4f4f4f;
}

.end-survey {
  width: 100%;
  background-color: #00c2ff;
  border-radius: 2px;
  text-transform: uppercase;
  color: #fff;
  border: 1px solid #00c2ff;
  min-height: 40px;
}

.end-survey:disabled {
  opacity: 0.5;
}
</style>
